import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus, Search, Filter, MoreVertical, Trash2, Edit, Copy,
  TrendingUp, TrendingDown, DollarSign, Eye, EyeOff, MousePointer, Users,
  RefreshCw, ExternalLink, AlertTriangle, CheckCircle, Clock, Target,
  BarChart3, Zap, ArrowUpRight, ArrowDownRight, Link2, Settings, Megaphone,
  ChevronRight, ChevronLeft, Loader2, Calendar, Play, Pause, FileDown
} from 'lucide-react';
import ConsolidatedReportModal from '../../components/campaigns/ConsolidatedReportModal';
import McpConnectCard from '../../components/campaigns/McpConnectCard';
import SyncFreshnessBar, { SyncFreshness } from '../../components/campaigns/SyncFreshnessBar';
import CampaignsPerformance from '../../components/campaigns/CampaignsPerformance';
import GoogleAccountPanel from '../../components/campaigns/google/GoogleAccountPanel';
import { GoogleAccountReport, GoogleReportCampaign, errorMessage as googleErrorMessage } from '../../components/campaigns/google/googleAccountTypes';
import { Sparkline } from '../../components/traffic-manager/tmCharts';
import type { DayPoint } from '../../services/trafficManagerService';
import { campaignService } from '../../services/campaignService';
import { useProject } from '../../contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import UsageBadge from '../../components/ui/UsageBadge';
import Select from '../../components/ui/Select';
import { Campaign } from '../../types';
import api from '../../services/api';
import { getJobResult } from '../../services/aiJobService';

// Platform icons
const GoogleAdsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" fill="#4285F4"/>
  </svg>
);

const MetaAdsIcon = () => (
  <img src="/logo_meta.png" alt="Meta" className="w-5 h-5" />
);

const LinkedInAdsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
  </svg>
);

const OpenAIAdsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
  </svg>
);

interface ConnectedAccount {
  id: string;
  platform: 'google_ads' | 'facebook_ads' | 'linkedin_ads' | 'openai_ads' | 'instagram' | 'linkedin';
  account_name: string;
  account_id: string;
  is_active: boolean;
  last_sync?: string;
}

interface PlatformCampaign extends Campaign {
  platform_account_id?: string;
  platform_campaign_id?: string;
  sync_status?: 'synced' | 'pending' | 'error';
  last_synced_at?: string;
}

// No mock data - using real backend data only

// "Encerrada": a campanha tem data de fim no passado. É o caso das campanhas de
// evento (ExpoRevestir, FIPAN...) que continuam marcadas como "ativas" no Meta/Google
// mas não entregam mais porque o flight venceu. Mostrar "ativa" nelas confunde.
const isEnded = (c: { end_date?: string; status?: string }): boolean => {
  if (!c?.end_date) return false;
  if (c.status === 'draft' || c.status === 'archived') return false;
  const end = new Date(c.end_date).getTime();
  if (Number.isNaN(end)) return false;
  return end < Date.now();
};

type PlatformTab = 'all' | 'google' | 'meta' | 'linkedin' | 'openai';

const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentProject } = useProject();

  // State
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [campaigns, setCampaigns] = useState<PlatformCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  // Progresso da sincronização em segundo plano (contas grandes levam 10+ min)
  const [syncProgress, setSyncProgress] = useState<{
    pct: number; etapa?: string; plataforma?: string | null; atual?: number; total?: number;
  } | null>(null);
  const syncJobRef = React.useRef<string | null>(null);
  // Sync disparado sozinho (dados velhos): sem a barra grande nem toasts de contagem.
  const syncQuietRef = React.useRef(false);
  const [freshness, setFreshness] = useState<SyncFreshness | null>(null);
  const metricsAtRef = React.useRef<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [daily, setDaily] = useState<{ diaria: DayPoint[]; series_por_campanha: Record<string, { date: string; spend: number }[]>; campanhas: number } | null>(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<PlatformTab>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState<'all' | '7d' | '30d' | '90d' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [metricsMap, setMetricsMap] = useState<Record<string, any>>({});
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsLoaded, setMetricsLoaded] = useState(false);
  const [showValues, setShowValues] = useState(true);
  const [showConsolidatedModal, setShowConsolidatedModal] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const [showContas, setShowContas] = useState(false);
  const [activeTotal, setActiveTotal] = useState<number | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Tab counts (total per platform)
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({ all: 0, google: 0, meta: 0, linkedin: 0, openai: 0 });

  // Sync state surfaced after a sync click — drives the "X campanhas pendentes,
  // tente em alguns minutos" banner instead of a red error toast.
  const [syncStatus, setSyncStatus] = useState<{
    metaPending: number;
    metaRateLimited: boolean;
    googleNeedsReconnect: boolean;
    lastSyncAt: number | null;
  }>({ metaPending: 0, metaRateLimited: false, googleNeedsReconnect: false, lastSyncAt: null });

  // Ad-account balances (Meta only has API; Google/LinkedIn don't expose)
  const [balances, setBalances] = useState<{
    meta?: {
      available: boolean;
      currency?: string;
      balance?: number | null;
      amount_spent?: number | null;
      spend_cap?: number | null;
      funding_source?: string | null;
      funding_type?: string | null;
      is_prepaid?: boolean;
      total_loaded?: number | null;
      promotional_credits?: Array<{ amount: number; currency: string; start_time?: string | null; end_time?: string | null; event_type?: string | null; product_type?: string | null }>;
      extended_credit?: { allocated: number; available: number; used: number; currency: string; credit_type?: string | null } | null;
      reason?: string;
    };
    google?: { available: boolean; reason?: string };
    linkedin?: { available: boolean; reason?: string };
  }>({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Load connected accounts once
  useEffect(() => {
    loadAccounts();
    loadBalances();
  }, [currentProject?.id]);

  const loadBalances = async () => {
    try {
      const params: Record<string, string> = {};
      if (currentProject?.id) params.project_id = currentProject.id;
      const res = await api.get('/campaigns/balances', { params });
      if (res.data?.success) setBalances(res.data.data || {});
    } catch {
      setBalances({});
    }
  };

  // Load campaigns when filters or pagination change
  useEffect(() => {
    loadCampaigns();
  }, [currentProject?.id, currentPage, rowsPerPage, debouncedSearch, statusFilter, activeTab, dateRange, customStartDate, customEndDate]);

  const loadAccounts = async () => {
    try {
      const params: Record<string, string> = { include_ads: 'true' };
      if (currentProject?.id) params.project_id = currentProject.id;
      const accountsRes = await api.get('/social/accounts', { params });
      if (accountsRes.data.success && accountsRes.data.data) {
        const filtered = accountsRes.data.data.filter((a: any) =>
          a.platform === 'google_ads' || a.platform === 'facebook_ads' || a.platform === 'linkedin_ads' || a.platform === 'linkedin' || a.platform === 'openai_ads'
        );
        setConnectedAccounts(filtered);
      } else {
        setConnectedAccounts([]);
      }
    } catch {
      setConnectedAccounts([]);
    }
  };

  const computeCreatedDateRange = (): { created_after?: string; created_before?: string } => {
    if (dateRange === 'all') return {};
    if (dateRange === 'custom') {
      if (!customStartDate && !customEndDate) return {};
      return {
        created_after: customStartDate || undefined,
        created_before: customEndDate || undefined
      };
    }
    const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;
    const start = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    return { created_after: start };
  };

  const loadCampaigns = async (quiet = false) => {
    if (!quiet) setIsLoading(true);
    try {
      const dateFilters = computeCreatedDateRange();
      const campaignsRes = await campaignService.getCampaigns({
        page: currentPage,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        platform: activeTab !== 'all' ? activeTab : undefined,
        ...dateFilters
      });
      if (campaignsRes.success && campaignsRes.data) {
        setCampaigns(campaignsRes.data);
        if (campaignsRes.pagination) {
          setTotalCampaigns(campaignsRes.pagination.total);
          setTotalPages(campaignsRes.pagination.totalPages);
        }
      } else {
        setCampaigns([]);
        setTotalCampaigns(0);
        setTotalPages(1);
      }
    } catch (error: any) {
      console.error('Error loading campaigns:', error);
      const message = error.response?.data?.message || t('campaigns.fetch_error', 'Erro ao carregar campanhas');
      toast.error(message);
      if (!quiet) {
        setCampaigns([]);
        setTotalCampaigns(0);
        setTotalPages(1);
      }
    } finally {
      if (!quiet) setIsLoading(false);
    }
  };

  // Quão novos são os dados (e o que mudou na última atualização).
  const loadFreshness = async (): Promise<SyncFreshness | null> => {
    try {
      const r = await api.get('/campaigns/sync/status');
      const d = r.data?.data || null;
      setFreshness(d);
      return d;
    } catch { return null; }
  };

  // Séries diárias para os gráficos e os minigráficos da lista (mesmo filtro da tabela).
  useEffect(() => {
    if (!currentProject?.id) return;
    let cancelado = false;
    setDailyLoading(true);
    api.get('/campaigns/performance/daily', { params: { days: 30, platform: activeTab !== 'all' ? activeTab : undefined, status: statusFilter || undefined, search: debouncedSearch || undefined, ...computeCreatedDateRange() } })
      .then((r) => { if (!cancelado) setDaily(r.data?.data || null); })
      .catch(() => { if (!cancelado) setDaily(null); })
      .finally(() => { if (!cancelado) setDailyLoading(false); });
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, activeTab, statusFilter, debouncedSearch, dateRange, customStartDate, customEndDate, refreshTick]);

  // Load tab counts (total per platform)
  const loadTabCounts = async () => {
    try {
      const dateFilters = computeCreatedDateRange();
      const baseParams = {
        limit: 1,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        ...dateFilters
      };
      campaignService.getCampaigns({ ...baseParams, status: 'active' })
        .then((r) => setActiveTotal(r.pagination?.total ?? null))
        .catch(() => setActiveTotal(null));
      const [allRes, googleRes, metaRes, linkedinRes, openaiRes] = await Promise.all([
        campaignService.getCampaigns({ ...baseParams }),
        campaignService.getCampaigns({ ...baseParams, platform: 'google' }),
        campaignService.getCampaigns({ ...baseParams, platform: 'meta' }),
        campaignService.getCampaigns({ ...baseParams, platform: 'linkedin' }),
        campaignService.getCampaigns({ ...baseParams, platform: 'openai' }),
      ]);
      setTabCounts({
        all: allRes.pagination?.total ?? 0,
        google: googleRes.pagination?.total ?? 0,
        meta: metaRes.pagination?.total ?? 0,
        linkedin: linkedinRes.pagination?.total ?? 0,
        openai: openaiRes.pagination?.total ?? 0,
      });
    } catch {
      // ignore - counts are non-critical
    }
  };

  // Load tab counts when filters change (but not on page change)
  useEffect(() => {
    loadTabCounts();
  }, [currentProject?.id, debouncedSearch, statusFilter, dateRange, customStartDate, customEndDate]);

  const loadData = async () => {
    await loadAccounts();
    await loadCampaigns();
  };


  // Resultado de uma sincronização (síncrona antiga ou do job em segundo plano)
  const finalizarSync = async (data: any, errors?: string[], quiet = false) => {
    // Sempre atualiza o banner de status (mesmo em sync parcial)
    setSyncStatus({
      metaPending: Number(data?.meta?.pending || 0),
      metaRateLimited: !!data?.metaRateLimited,
      googleNeedsReconnect: !!data?.google?.needsReconnect,
      lastSyncAt: Date.now()
    });
    const erros = errors || [
      ...(data?.google?.errors || []).map((e: string) => `Google: ${e}`),
      ...(data?.meta?.errors || []).map((e: string) => `Meta: ${e}`),
      ...(data?.linkedin?.errors || []).map((e: string) => `LinkedIn: ${e}`),
      ...(data?.openai?.errors || []).map((e: string) => `ChatGPT Ads: ${e}`),
    ];

    if (quiet) {
      // Atualização sozinha: só avisa se algo mudou de verdade (o detalhe fica na faixa).
      const mud = (data?.changes || []).filter((c: any) => c.kind !== 'spend');
      if (mud.length) toast.success(t('campaigns.freshness.toast', 'Dados atualizados: {{n}} mudança(s) nas plataformas', { n: mud.length }));
      await loadCampaigns(true);
      setRefreshTick((x) => x + 1);
      loadFreshness();
      return;
    }
    if (data?.total > 0) {
      const parts = [];
      const fmt = (label: string, p: any) => {
        if (!p?.synced) return null;
        const ads = p.adsSynced ? `, ${p.adsSynced} ${t('campaigns.sync.ads', 'anúncios')}` : '';
        return `${label}: ${p.synced}${ads}`;
      };
      const g = fmt('Google', data.google); if (g) parts.push(g);
      const m = fmt('Meta', data.meta); if (m) parts.push(m);
      const l = fmt('LinkedIn', data.linkedin); if (l) parts.push(l);
      const o = fmt('ChatGPT', data.openai); if (o) parts.push(o);
      toast.success(`${data.total} ${t('campaigns.sync.campaignsSynced', 'campanhas sincronizadas')} (${parts.join(', ')})`);
    } else if (data?.metaRateLimited) {
      // Não mostra "erro" — só info. O banner em cima da página explica.
    } else if (erros.length > 0) {
      toast.error(erros[0]);
    } else {
      toast.success(t('campaigns.sync.noCampaigns', 'Nenhuma campanha encontrada nas plataformas'));
    }

    await loadData();
    setRefreshTick((x) => x + 1);
    loadFreshness();
  };

  // Acompanha o job de sincronização até terminar, atualizando a barra de progresso.
  // Para sozinho se o usuário trocar de projeto ou sair da página (syncJobRef muda).
  const acompanharSync = async (jobId: string, quiet = false) => {
    syncJobRef.current = jobId;
    syncQuietRef.current = quiet;
    setIsSyncing(true);
    setSyncProgress((p) => p || { pct: 1, etapa: 'inicio' });
    const inicio = Date.now();
    let ultimaLista = Date.now();
    try {
      while (syncJobRef.current === jobId) {
        if (Date.now() - inicio > 60 * 60 * 1000) {
          throw new Error(t('campaigns.sync.tooLong', 'A sincronização está demorando mais que o normal. Ela continua no servidor — volte em alguns minutos.') as string);
        }
        const r = await getJobResult(jobId);
        if (syncJobRef.current !== jobId) return;
        if (r.completed) {
          if (r.success) await finalizarSync(r.data, undefined, quiet);
          else throw new Error(r.error || (t('campaigns.sync.error', 'Erro ao sincronizar campanhas') as string));
          return;
        }
        const pr: any = r.progress;
        if (pr && typeof pr === 'object') setSyncProgress(pr);
        else if (typeof pr === 'number' && pr > 0) setSyncProgress({ pct: pr });
        // a lista vai se enchendo enquanto sincroniza
        if (Date.now() - ultimaLista > 20000) { ultimaLista = Date.now(); loadCampaigns(); loadTabCounts(); }
        await new Promise((res) => setTimeout(res, 2000));
      }
    } catch (error: any) {
      if (syncJobRef.current !== jobId) return;
      if (quiet) { loadFreshness(); return; }
      const message = error.response?.data?.error || error.response?.data?.message || error.message || t('campaigns.sync.error', 'Erro ao sincronizar campanhas');
      toast.error(message);
    } finally {
      if (syncJobRef.current === jobId) {
        syncJobRef.current = null;
        syncQuietRef.current = false;
        setIsSyncing(false);
        setSyncProgress(null);
      }
    }
  };

  const handleSync = async (platform?: 'google' | 'meta') => {
    setIsSyncing(true);
    try {
      // Em segundo plano: o servidor responde na hora com o jobId (ou o da
      // sincronização que já está rodando) e a tela acompanha o progresso.
      const response = await api.post('/campaigns/sync', { platform, async: true });
      if (response.data?.async && response.data?.jobId) {
        await acompanharSync(response.data.jobId);
        return;
      }
      await finalizarSync(response.data?.data, response.data?.errors);
    } catch (error: any) {
      const message = error.response?.data?.message || t('campaigns.sync.error', 'Erro ao sincronizar campanhas');
      toast.error(message);
    } finally {
      if (!syncJobRef.current) setIsSyncing(false);
    }
  };

  // Ao abrir a página (ou trocar de projeto), retoma a barra de uma sincronização que
  // ainda está rodando — por exemplo depois de recarregar a página no meio dela.
  useEffect(() => {
    syncJobRef.current = null;
    setIsSyncing(false);
    setSyncProgress(null);
    if (!currentProject?.id) return;
    let cancelado = false;
    loadFreshness().then(async (st) => {
      if (cancelado || !st) return;
      metricsAtRef.current = st.metrics_synced_at;
      if (st.running?.jobId) {
        // Retoma uma que já está rodando (botão ou automática), sem a barra grande se foi automática.
        if (st.running.progress && typeof st.running.progress === 'object') setSyncProgress(st.running.progress);
        // Só a sync automática (cron ou tela) segue em silêncio; a do botão volta com a barra e os avisos.
        acompanharSync(st.running.jobId, st.running.trigger !== 'manual');
        return;
      }
      // Dados velhos: puxa de novo sozinho, em segundo plano.
      if (st.stale) {
        try {
          const r = await api.post('/campaigns/sync', { async: true, trigger: 'stale' });
          if (!cancelado && r.data?.jobId) acompanharSync(r.data.jobId, true);
        } catch { /* fica o botão */ }
      }
    });
    return () => { cancelado = true; syncJobRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id]);

  // Com a tela aberta: a cada 2 min confere se o cron trouxe métricas novas (e
  // atualiza a lista sem piscar) ou se os dados ficaram velhos (puxa sozinho).
  useEffect(() => {
    if (!currentProject?.id) return;
    const id = setInterval(async () => {
      if (document.visibilityState !== 'visible' || syncJobRef.current) return;
      const st = await loadFreshness();
      if (!st) return;
      if (st.metrics_synced_at && st.metrics_synced_at !== metricsAtRef.current) {
        metricsAtRef.current = st.metrics_synced_at;
        await loadCampaigns(true);
        setRefreshTick((x) => x + 1);
      }
      if (st.running?.jobId) { acompanharSync(st.running.jobId, st.running.trigger !== 'manual'); return; }
      if (st.stale) {
        try {
          const r = await api.post('/campaigns/sync', { async: true, trigger: 'stale' });
          if (r.data?.jobId) acompanharSync(r.data.jobId, true);
        } catch { /* tenta no próximo ciclo */ }
      }
    }, 120000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, currentPage, rowsPerPage, debouncedSearch, statusFilter, activeTab, dateRange, customStartDate, customEndDate]);

  const textoProgresso = (p: NonNullable<typeof syncProgress>) => {
    if (p.etapa === 'campanhas' && p.total) {
      return t('campaigns.sync.progressCampaigns', 'Sincronizando {{plataforma}}: campanha {{atual}} de {{total}}', { plataforma: p.plataforma || '', atual: Math.min((p.atual || 0) + 1, p.total), total: p.total });
    }
    if (p.etapa === 'metricas' && p.total) {
      return t('campaigns.sync.progressMetrics', 'Atualizando métricas: {{atual}} de {{total}} campanhas', { atual: Math.min((p.atual || 0) + 1, p.total), total: p.total });
    }
    if (p.etapa === 'fim') return t('campaigns.sync.progressDone', 'Finalizando...');
    return t('campaigns.sync.progressStart', 'Buscando campanhas nas plataformas...');
  };

  const computeMetricsDateRange = (): { startDate: string; endDate: string } | null => {
    const end = new Date().toISOString().split('T')[0];
    if (dateRange === 'all') {
      // Default to last 30 days for metrics
      const start = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
      return { startDate: start, endDate: end };
    }
    if (dateRange === 'custom') {
      if (!customStartDate || !customEndDate || customStartDate > customEndDate) return null;
      return { startDate: customStartDate, endDate: customEndDate };
    }
    const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;
    const start = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    return { startDate: start, endDate: end };
  };

  // Track latest request to avoid race conditions
  const metricsRequestRef = React.useRef(0);

  // Fetch metrics whenever date range or campaigns change
  useEffect(() => {
    if (campaigns.length === 0) return;
    const range = computeMetricsDateRange();
    if (!range) return;

    const requestId = ++metricsRequestRef.current;
    setIsLoadingMetrics(true);

    campaignService.getCampaignsMetricsSummary(range.startDate, range.endDate)
      .then((res) => {
        if (requestId !== metricsRequestRef.current) return; // stale request
        if (res.success && res.data?.campaigns) {
          setMetricsMap(res.data.campaigns);
        } else {
          setMetricsMap({});
        }
        setMetricsLoaded(true);
      })
      .catch((error) => {
        if (requestId !== metricsRequestRef.current) return;
        console.error('Error loading metrics:', error);
        setMetricsMap({});
        setMetricsLoaded(true);
      })
      .finally(() => {
        if (requestId === metricsRequestRef.current) {
          setIsLoadingMetrics(false);
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStartDate, customEndDate, campaigns.length, refreshTick]);

  // Relatório da conta Google Ads (Google Ads API): uma busca por período, usada pelo
  // painel "Conta Google Ads" e pela coluna "Todas conv." da tabela.
  const [googleReport, setGoogleReport] = useState<GoogleAccountReport | null>(null);
  const [googleReportLoading, setGoogleReportLoading] = useState(false);
  const [googleReportError, setGoogleReportError] = useState<string | null>(null);
  const [googleReportTick, setGoogleReportTick] = useState(0);
  const googleReportRequestRef = React.useRef(0);
  const googleConnected = connectedAccounts.some(a => a.platform === 'google_ads');
  const googleRange = computeMetricsDateRange();

  useEffect(() => {
    if (!googleConnected || !googleRange) return;
    const requestId = ++googleReportRequestRef.current;
    setGoogleReportLoading(true);
    setGoogleReportError(null);
    api.get('/campaigns/google/account-report', { params: { start: googleRange.startDate, end: googleRange.endDate } })
      .then((res) => {
        if (requestId !== googleReportRequestRef.current) return;
        const data = res.data?.data ?? res.data;
        setGoogleReport({ account: data?.account || null, campaigns: Array.isArray(data?.campaigns) ? data.campaigns : [] });
      })
      .catch((err) => {
        if (requestId !== googleReportRequestRef.current) return;
        setGoogleReport(null);
        setGoogleReportError(googleErrorMessage(err, t('campaigns.google.reportError', 'Não foi possível carregar o relatório da conta Google Ads.')));
      })
      .finally(() => {
        if (requestId === googleReportRequestRef.current) setGoogleReportLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConnected, googleRange?.startDate, googleRange?.endDate, currentProject?.id, googleReportTick]);

  const googleReportByCampaign = React.useMemo(() => {
    const map: Record<string, GoogleReportCampaign> = {};
    (googleReport?.campaigns || []).forEach((c) => { map[String(c.id).replace(/\D/g, '')] = c; });
    return map;
  }, [googleReport]);

  const googleAllConversions = (campaign: PlatformCampaign): string => {
    const ext = String(campaign.external_id_google || '').replace(/\D/g, '');
    if (!ext) return '—';
    if (!googleReport) return googleReportLoading ? '…' : '—';
    const row = googleReportByCampaign[ext];
    if (!row) return '0';
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(Number(row.all_conversions) || 0);
  };

  const [deletingCampaigns, setDeletingCampaigns] = useState<Set<string>>(new Set());
  const [togglingCampaigns, setTogglingCampaigns] = useState<Set<string>>(new Set());

  const handleToggleStatus = async (campaign: PlatformCampaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    const projectId = currentProject?.id || '';
    setTogglingCampaigns(prev => new Set(prev).add(campaign.id));

    const toastId = toast.loading(
      newStatus === 'active'
        ? t('campaigns.toasts.activating', 'Ativando campanha...')
        : t('campaigns.toasts.pausing', 'Pausando campanha...'),
      { id: `toggle-${campaign.id}` }
    );

    try {
      const result = await campaignService.togglePlatformStatus(projectId, campaign.id, newStatus);
      const platformResults = result.data?.platformDetails || result.data?.platforms || {};
      const hasErrors = Object.values(platformResults).some((r: any) => !r.skipped && !r.success);
      const localUpdated = result.data?.localUpdated !== false;

      if (hasErrors && !localUpdated) {
        toast.error(
          t('campaigns.toasts.platformSyncFailed', 'Status não foi alterado.'),
          { id: toastId, duration: 4000 }
        );
      } else if (hasErrors) {
        toast.error(t('campaigns.toggleResult.hasErrors', 'Atualizado com erros em algumas plataformas'), { id: toastId, duration: 4000 });
      } else {
        toast.success(
          result.message || (newStatus === 'active' ? t('campaigns.toasts.activatedOnPlatform') : t('campaigns.toasts.pausedOnPlatform')),
          { id: toastId, duration: 3000 }
        );
      }

      // Optimistic update
      setCampaigns(prev => prev.map(c =>
        c.id === campaign.id ? { ...c, status: localUpdated ? newStatus : c.status } : c
      ));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('campaigns.toasts.platformError', 'Erro ao atualizar campanha'),
        { id: toastId, duration: 4000 }
      );
    } finally {
      setTogglingCampaigns(prev => {
        const next = new Set(prev);
        next.delete(campaign.id);
        return next;
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('campaigns.toasts.confirmDelete', 'Are you sure you want to delete this campaign?'))) return;
    setDeletingCampaigns(prev => new Set(prev).add(id));
    try {
      await campaignService.deleteCampaign(id);
      toast.success(t('campaigns.toasts.deleted', 'Campaign deleted'));
      loadCampaigns();
    } catch (error) {
      console.error('Delete campaign error:', error);
      toast.error(t('campaigns.toasts.deleteError', 'Error deleting campaign'));
    } finally {
      setDeletingCampaigns(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const [duplicatingCampaigns, setDuplicatingCampaigns] = useState<Set<string>>(new Set());
  const handleDuplicate = async (id: string) => {
    setDuplicatingCampaigns(prev => new Set(prev).add(id));
    try {
      const response = await campaignService.duplicateCampaign(id);
      if (response.success) {
        toast.success(t('campaigns.toasts.duplicated', 'Campanha duplicada como rascunho'));
        loadCampaigns();
      } else {
        toast.error(response.message || t('campaigns.toasts.duplicateError', 'Erro ao duplicar campanha'));
      }
    } catch (error: any) {
      console.error('Duplicate campaign error:', error);
      toast.error(error.response?.data?.message || t('campaigns.toasts.duplicateError', 'Erro ao duplicar campanha'));
    } finally {
      setDuplicatingCampaigns(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleConnectAccount = async (platform: 'google_ads' | 'facebook_ads' | 'linkedin_ads' | 'openai_ads') => {
    // ChatGPT Ads conecta por API key (sem OAuth) — abre direto o modal
    // na aba de Integrações via query param
    if (platform === 'openai_ads') {
      navigate('/settings/integrations?connect=openai_ads');
      return;
    }
    try {
      toast.loading(t('common.loading'), { id: 'oauth' });
      // Pass project_id so integrations are per-project
      const params = currentProject?.id ? { project_id: currentProject.id } : {};
      const response = await api.get(`/oauth/${platform}/authorize`, { params });
      if (response.data.success && response.data.authUrl) {
        toast.dismiss('oauth');
        window.location.href = response.data.authUrl;
      } else {
        throw new Error(response.data.message || t('errors.generic'));
      }
    } catch (error: any) {
      console.error('OAuth error:', error);
      toast.error(error.response?.data?.message || t('errors.connectionError'), { id: 'oauth' });
    }
  };

  const handleNewCampaign = () => {
    // Always go to new campaign page and let user choose platform in step 0
    navigate('/campaigns/new');
  };

  // Campaigns are already filtered server-side
  const filteredCampaigns = campaigns;

  // Stats by platform (campaigns can be in multiple platforms now)
  const googleCampaigns = campaigns.filter(c => (c.platforms || [c.platform]).includes('google'));
  const metaCampaigns = campaigns.filter(c => (c.platforms || [c.platform]).includes('meta'));
  const linkedinCampaigns = campaigns.filter(c => (c.platforms || [c.platform]).includes('linkedin'));
  const openaiCampaigns = campaigns.filter(c => (c.platforms || [c.platform]).includes('openai'));

  const safeNumber = (val: any): number => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const getMetric = (c: PlatformCampaign, field: string, fallbackField?: string) => {
    if (metricsLoaded) {
      // Date filter is active and we have a response — use filtered data (even if zeros)
      return safeNumber(metricsMap[c.id]?.[field] ?? 0);
    }
    // No metrics loaded yet — use all-time totals as initial display
    return safeNumber((c as any)[fallbackField || field]);
  };

  // Números do topo: quando as métricas do período chegaram, soma TODAS as campanhas
  // (metricsMap vem do servidor com todas). Antes somava só as 25 da página aberta.
  const allMetrics = Object.values(metricsMap) as any[];
  const sumAll = (field: string) => allMetrics.reduce((sum, m) => sum + safeNumber(m?.[field]), 0);
  const stats = {
    totalSpend: metricsLoaded ? sumAll('spend') : campaigns.reduce((sum, c) => sum + getMetric(c, 'spend', 'total_spent'), 0),
    totalConversions: metricsLoaded ? sumAll('conversions') : campaigns.reduce((sum, c) => sum + getMetric(c, 'conversions', 'total_conversions'), 0),
    totalClicks: metricsLoaded ? sumAll('clicks') : campaigns.reduce((sum, c) => sum + getMetric(c, 'clicks', 'total_clicks'), 0),
    activeCampaigns: activeTotal ?? campaigns.filter(c => c.status === 'active' && !isEnded(c)).length,
    googleSpend: metricsLoaded ? sumAll('googleSpend') : campaigns.reduce((sum, c) => sum + (googleCampaigns.includes(c) ? safeNumber(c.total_spent) : 0), 0),
    metaSpend: metricsLoaded ? sumAll('metaSpend') : campaigns.reduce((sum, c) => sum + (metaCampaigns.includes(c) ? safeNumber(c.total_spent) : 0), 0),
    linkedinSpend: metricsLoaded ? sumAll('linkedinSpend') : campaigns.reduce((sum, c) => sum + (linkedinCampaigns.includes(c) ? safeNumber(c.total_spent) : 0), 0),
    openaiSpend: metricsLoaded ? sumAll('openaiSpend') : campaigns.reduce((sum, c) => sum + (openaiCampaigns.includes(c) ? safeNumber(c.total_spent) : 0), 0),
  };

  const formatCurrency = (value: number) => {
    if (!showValues) return 'R$ •••••';
    const safeVal = isNaN(value) ? 0 : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(safeVal);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const hasGoogleAds = connectedAccounts.some(a => a.platform === 'google_ads');
  const hasMetaAds = connectedAccounts.some(a => a.platform === 'facebook_ads');
  const hasLinkedInAds = connectedAccounts.some(a => a.platform === 'linkedin_ads');
  const hasOpenaiAds = connectedAccounts.some(a => a.platform === 'openai_ads');
  const hasAnyConnection = hasGoogleAds || hasMetaAds || hasLinkedInAds || hasOpenaiAds;

  // If no accounts connected, show connection prompt
  if (!isLoading && !hasAnyConnection) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{t('campaigns.title')}</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{t('campaigns.page.subtitle')}</p>
        </div>

        <Card className="border-2 border-dashed border-gray-300 dark:border-gray-600">
          <CardContent className="pt-8 pb-8 sm:pt-12 sm:pb-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-50 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Megaphone className="w-8 h-8 sm:w-10 sm:h-10 text-primary-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('campaigns.page.connectTitle')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-8">
              {t('campaigns.page.connectDescription')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
              <button
                onClick={() => handleConnectAccount('google_ads')}
                className="flex items-center gap-3 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all w-full sm:w-auto"
              >
                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <GoogleAdsIcon />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 dark:text-white">Google Ads</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.accounts.googleTagline', 'Pesquisa do Google, sites e YouTube')}</p>
                </div>
              </button>

              <button
                onClick={() => handleConnectAccount('facebook_ads')}
                className="flex items-center gap-3 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all w-full sm:w-auto"
              >
                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <MetaAdsIcon />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 dark:text-white">Meta Ads</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Facebook, Instagram</p>
                </div>
              </button>

              <button
                onClick={() => handleConnectAccount('linkedin_ads')}
                className="flex items-center gap-3 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all w-full sm:w-auto"
              >
                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <LinkedInAdsIcon />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 dark:text-white">LinkedIn Ads</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.accounts.linkedinTagline', 'Posts patrocinados e mensagens')}</p>
                </div>
              </button>

              <button
                onClick={() => handleConnectAccount('openai_ads')}
                className="flex items-center gap-3 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all w-full sm:w-auto"
              >
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <OpenAIAdsIcon />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 dark:text-white">ChatGPT Ads</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.accounts.openaiTagline', 'Anúncios no ChatGPT')}</p>
                </div>
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-6">
              {t('campaigns.page.credentialsProtected')}
            </p>
          </CardContent>
        </Card>

        {/* Também aqui: quem ainda não conectou conta é justamente quem mais ganha
            em plugar o assistente de IA — sem isto o card só existia pra quem já
            tinha Google/Meta ligado. */}
        <McpConnectCard />
      </div>
    );
  }

  // Ações da linha (usadas na tabela do computador e nos cartões do celular)
  // platforms já chegou como texto ("meta") em registros antigos: sempre lista.
  const plataformasDa = (c: PlatformCampaign): string[] => {
    const v: any = c.platforms;
    if (Array.isArray(v) && v.length) return v;
    if (typeof v === 'string' && v) return v.split(',').map((x) => x.trim()).filter(Boolean);
    return c.platform ? [c.platform] : [];
  };
  const isOpenaiOnly = (c: PlatformCampaign) => plataformasDa(c).every(p => p === 'openai');
  const statusBadge = (campaign: PlatformCampaign) => isEnded(campaign) ? (
    <Badge variant="gray">{t('campaigns.status.ended', 'Encerrada')}</Badge>
  ) : (
    <Badge variant={campaign.status === 'active' ? 'success' : campaign.status === 'paused' ? 'warning' : 'gray'}>
      {campaign.status === 'active' ? t('campaigns.status.active') : campaign.status === 'paused' ? t('campaigns.status.paused') : t('campaigns.status.draft')}
    </Badge>
  );
  const platformIcons = (campaign: PlatformCampaign) => {
    const list = plataformasDa(campaign);
    const items: Array<[string, React.ReactNode, string | undefined, string]> = [
      ['google', <GoogleAdsIcon key="g" />, campaign.external_id_google, 'Google Ads'],
      ['meta', <MetaAdsIcon key="m" />, campaign.external_id_meta, 'Meta Ads'],
      ['linkedin', <LinkedInAdsIcon key="l" />, campaign.external_id_linkedin, 'LinkedIn Ads'],
      ['openai', <OpenAIAdsIcon key="o" />, campaign.external_id_openai, 'ChatGPT Ads'],
    ];
    return (
      <div className="flex -space-x-1 shrink-0">
        {items.filter(([p]) => list.includes(p as any)).map(([p, icon, ext, label]) => (
          <div key={p} className="relative w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center border-2 border-white dark:border-gray-900" title={ext ? `Publicada no ${label}` : `Não publicada no ${label}`}>
            {icon}
            <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${ext ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'}`} />
          </div>
        ))}
      </div>
    );
  };
  const publishInfo = (campaign: PlatformCampaign) => {
    const platforms = plataformasDa(campaign);
    const ext: Record<string, string | undefined> = { google: campaign.external_id_google, meta: campaign.external_id_meta, linkedin: campaign.external_id_linkedin, openai: campaign.external_id_openai };
    const names: Record<string, string> = { google: 'Google', meta: 'Meta', linkedin: 'LinkedIn', openai: 'ChatGPT' };
    const notPublished = platforms.filter((p) => !ext[p as string]).map((p) => names[p as string] || p);
    if (notPublished.length === 0) return <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle className="w-3 h-3" />Publicada</span>;
    if (notPublished.length === platforms.length) return <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500"><Clock className="w-3 h-3" />Não publicada</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400"><AlertTriangle className="w-3 h-3" />Falta publicar: {notPublished.join(', ')}</span>;
  };
  const rowActions = (campaign: PlatformCampaign, big = false) => {
    const pad = big ? 'p-2.5' : 'p-2';
    return (
      <div className="flex items-center gap-1">
        {(campaign.status === 'active' || campaign.status === 'paused') && (
          <button
            onClick={() => handleToggleStatus(campaign)}
            disabled={togglingCampaigns.has(campaign.id)}
            className={`${pad} rounded-lg disabled:opacity-50 transition-colors ${campaign.status === 'active' ? 'hover:bg-yellow-100 dark:hover:bg-yellow-900/20 text-yellow-600' : 'hover:bg-green-100 dark:hover:bg-green-900/20 text-green-600'}`}
            title={campaign.status === 'active' ? t('campaigns.actions.pause') : t('campaigns.actions.activate')}
            aria-label={campaign.status === 'active' ? t('campaigns.actions.pause') : t('campaigns.actions.activate')}
          >
            {togglingCampaigns.has(campaign.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : campaign.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
        )}
        {/* Campanha só do ChatGPT: editor/duplicação são fase 2 */}
        {!isOpenaiOnly(campaign) && (
          <Link to={`/campaigns/${campaign.id}/edit`} className={`${pad} hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500`} title={t('common.edit', 'Editar')} aria-label={t('common.edit', 'Editar')}>
            <Edit className="w-4 h-4" />
          </Link>
        )}
        {!isOpenaiOnly(campaign) && (
          <button onClick={() => handleDuplicate(campaign.id)} disabled={duplicatingCampaigns.has(campaign.id)} className={`${pad} hover:bg-purple-100 dark:hover:bg-purple-900/20 rounded-lg text-gray-500 hover:text-purple-600 disabled:opacity-50`} title={t('campaigns.actions.duplicate', 'Duplicar campanha')} aria-label={t('campaigns.actions.duplicate', 'Duplicar campanha')}>
            {duplicatingCampaigns.has(campaign.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
          </button>
        )}
        <button onClick={() => handleDelete(campaign.id)} disabled={deletingCampaigns.has(campaign.id)} className={`${pad} hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg text-red-500 disabled:opacity-50`} title={t('common.delete', 'Excluir')} aria-label={t('common.delete', 'Excluir')}>
          {deletingCampaigns.has(campaign.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </div>
    );
  };

  const connected = [
    hasGoogleAds && { key: 'google' as PlatformTab, label: 'Google', icon: <GoogleAdsIcon /> },
    hasMetaAds && { key: 'meta' as PlatformTab, label: 'Meta', icon: <MetaAdsIcon /> },
    hasLinkedInAds && { key: 'linkedin' as PlatformTab, label: 'LinkedIn', icon: <LinkedInAdsIcon /> },
    hasOpenaiAds && { key: 'openai' as PlatformTab, label: 'ChatGPT', icon: <OpenAIAdsIcon /> },
  ].filter(Boolean) as Array<{ key: PlatformTab; label: string; icon: React.ReactNode }>;
  const notConnected = [
    !hasGoogleAds && { key: 'google_ads' as const, label: 'Google Ads', icon: <GoogleAdsIcon /> },
    !hasMetaAds && { key: 'facebook_ads' as const, label: 'Meta Ads', icon: <MetaAdsIcon /> },
    !hasLinkedInAds && { key: 'linkedin_ads' as const, label: 'LinkedIn Ads', icon: <LinkedInAdsIcon /> },
    !hasOpenaiAds && { key: 'openai_ads' as const, label: 'ChatGPT Ads', icon: <OpenAIAdsIcon /> },
  ].filter(Boolean) as Array<{ key: 'google_ads' | 'facebook_ads' | 'linkedin_ads' | 'openai_ads'; label: string; icon: React.ReactNode }>;
  const spendByPlatform = [
    hasGoogleAds && ['Google Ads', stats.googleSpend],
    hasMetaAds && ['Meta Ads', stats.metaSpend],
    hasLinkedInAds && ['LinkedIn Ads', stats.linkedinSpend],
    hasOpenaiAds && ['ChatGPT Ads', stats.openaiSpend],
  ].filter(Boolean) as Array<[string, number]>;
  const selectCls = 'w-full sm:w-auto px-3 py-2.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Cabeçalho: título + uma ação principal. O resto vai no menu "⋯" */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{t('campaigns.title')}</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{t('campaigns.page.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden md:block"><UsageBadge resource="campaigns" /></div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleSync()}
            disabled={isSyncing}
            leftIcon={isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            aria-label={t('campaigns.sync.button') as string}
            className="h-10"
          >
            <span className="hidden sm:inline">
              {isSyncing ? `${t('campaigns.sync.syncing')}${syncProgress?.pct ? ` ${Math.min(99, syncProgress.pct)}%` : ''}` : t('campaigns.sync.button')}
            </span>
          </Button>
          <div className="relative">
            <button
              onClick={() => setMenuAberto((v) => !v)}
              className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              aria-label={t('common.more', 'Mais opções') as string}
              aria-expanded={menuAberto}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {menuAberto && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuAberto(false)} />
                <div className="absolute right-0 top-12 z-40 w-60 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1">
                  <button onClick={() => { setMenuAberto(false); setShowConsolidatedModal(true); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <FileDown className="w-4 h-4" /> {t('campaigns.consolidatedReport.button', 'Exportar Consolidado')}
                  </button>
                  <button onClick={() => { setMenuAberto(false); navigate('/short-links'); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Link2 className="w-4 h-4" /> {t('shortLinks.openShortcut', 'Encurtador de Links')}
                  </button>
                  <button onClick={() => { setMenuAberto(false); setShowValues(!showValues); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    {showValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {showValues ? t('dashboard.hideValues') : t('dashboard.showValues')}
                  </button>
                  {notConnected.length > 0 && <div className="my-1 border-t border-gray-100 dark:border-gray-700" />}
                  {notConnected.map((p) => (
                    <button key={p.key} onClick={() => { setMenuAberto(false); handleConnectAccount(p.key); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      {p.icon} {t('campaigns.accounts.connect', 'Conectar')} {p.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <Button onClick={handleNewCampaign} leftIcon={<Plus className="w-4 h-4" />} className="h-10">
            <span className="sm:hidden">{t('common.new', 'Nova')}</span>
            <span className="hidden sm:inline">{t('campaigns.newCampaign')}</span>
          </Button>
        </div>
      </div>

      {/* Progresso da sincronização em segundo plano */}
      {isSyncing && syncProgress && !syncQuietRef.current && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4" role="status" aria-live="polite">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">{textoProgresso(syncProgress)}</span>
            </div>
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 tabular-nums">{Math.min(99, syncProgress.pct || 0)}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-blue-100 dark:bg-blue-900/50 overflow-hidden">
            <div className="h-full rounded-full bg-blue-600 dark:bg-blue-500 transition-all duration-700" style={{ width: `${Math.max(2, Math.min(99, syncProgress.pct || 0))}%` }} />
          </div>
          <p className="text-xs text-blue-800/80 dark:text-blue-200/80 mt-2">
            {t('campaigns.sync.progressHint', 'Contas com muitas campanhas podem levar alguns minutos. Pode continuar usando a plataforma: a sincronização segue em segundo plano.')}
          </p>
        </div>
      )}

      {/* Google Ads precisa reconectar — token sem scope `adwords` ou customer_id inválido.
          Refresh não resolve; cliente tem que desconectar e reautorizar. */}
      {syncStatus.googleNeedsReconnect && (
        <div className="rounded-lg border border-red-200 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20 p-4 flex flex-col sm:flex-row sm:items-start gap-3">
          <AlertTriangle className="hidden sm:block w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">{t('campaigns.sync.googleReconnectTitle', 'Reconecte o Google Ads')}</p>
            <p className="text-xs text-red-800 dark:text-red-200 mt-1">
              {t('campaigns.sync.googleReconnectMsg', 'A conexão do Google Ads não autorizou o acesso às campanhas. Desconecte e conecte novamente em Integrações pra autorizar o Google Ads e selecionar a conta.')}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate('/content/integrations')} leftIcon={<ExternalLink className="w-4 h-4" />}>
            {t('campaigns.sync.reconnect', 'Reconectar')}
          </Button>
        </div>
      )}

      {/* Meta limitou as chamadas e ainda há campanhas pendentes — aviso calmo, não erro */}
      {syncStatus.metaRateLimited && syncStatus.metaPending > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 p-4 flex flex-col sm:flex-row sm:items-start gap-3">
          <Clock className="hidden sm:block w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">{t('campaigns.sync.metaPendingTitle', 'Sync parcial da Meta')}</p>
            <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
              {t('campaigns.sync.metaPendingMsg', '{{n}} campanhas ainda pendentes. A Meta limitou as chamadas — clique em "Sincronizar" novamente em alguns minutos pra trazer o restante.', { n: syncStatus.metaPending })}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => handleSync()} disabled={isSyncing} leftIcon={<RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />}>
            {t('campaigns.sync.button', 'Sincronizar')}
          </Button>
        </div>
      )}

      {/* Quão novos são os dados e o que mudou na última atualização */}
      <SyncFreshnessBar f={freshness} syncing={isSyncing} onSyncNow={() => handleSync()} currency={campaigns.find((c: any) => c.currency)?.currency || 'BRL'} />

      {/* Números do período (somados de TODAS as campanhas, não só da página) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 truncate">{t('campaigns.stats.totalSpend')}</p>
            <DollarSign className="hidden sm:block w-5 h-5 text-blue-500 shrink-0" />
          </div>
          <p className="text-lg sm:text-2xl font-bold text-blue-700 dark:text-blue-300 truncate">{formatCurrency(stats.totalSpend)}</p>
          {spendByPlatform.length > 1 && (
            <div className="hidden sm:block space-y-0.5 border-t border-blue-200 dark:border-blue-700/50 mt-2 pt-2">
              {spendByPlatform.map(([label, v]) => (
                <div key={label} className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">{label}</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-green-200 dark:border-green-800 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 truncate">{t('campaigns.stats.conversions')}</p>
            <Target className="hidden sm:block w-5 h-5 text-green-500 shrink-0" />
          </div>
          <p className="text-lg sm:text-2xl font-bold text-green-700 dark:text-green-300">{formatNumber(stats.totalConversions)}</p>
          <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mt-1">{t('campaigns.stats.periodTotal')}</p>
        </div>
        <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 truncate">{t('campaigns.stats.clicks', 'Cliques')}</p>
            <MousePointer className="hidden sm:block w-5 h-5 text-purple-500 shrink-0" />
          </div>
          <p className="text-lg sm:text-2xl font-bold text-purple-700 dark:text-purple-300">{formatNumber(stats.totalClicks)}</p>
          <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mt-1">{t('campaigns.stats.periodTotal')}</p>
        </div>
        <div className="rounded-xl border border-orange-200 dark:border-orange-800 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400 truncate">{t('campaigns.stats.activeCampaigns')}</p>
            <Zap className="hidden sm:block w-5 h-5 text-orange-500 shrink-0" />
          </div>
          <p className="text-lg sm:text-2xl font-bold text-orange-700 dark:text-orange-300">{stats.activeCampaigns}</p>
          <p className="hidden sm:block text-xs text-orange-600 dark:text-orange-400 mt-1">{t('campaigns.stats.ofTotal', { total: tabCounts.all })}</p>
        </div>
      </div>

      {showValues && (
        <CampaignsPerformance diaria={daily?.diaria || null} loading={dailyLoading} currency={campaigns.find((c: any) => c.currency)?.currency || 'BRL'} count={daily?.campanhas || 0} />
      )}

      {googleConnected && (
        <GoogleAccountPanel
          report={googleReport}
          reportLoading={googleReportLoading}
          reportError={googleReportError}
          onReloadReport={() => setGoogleReportTick((n) => n + 1)}
          range={googleRange}
          showValues={showValues}
        />
      )}

      {/* Filtros numa caixa só: plataforma (só as conectadas), busca, situação e período */}
      <Card padding={false}>
        <div className="p-3 sm:p-4 space-y-3">
          {connected.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {[{ key: 'all' as PlatformTab, label: t('campaigns.tabs.all') as string, icon: null as React.ReactNode }, ...connected].map((p) => (
                <button
                  key={p.key}
                  onClick={() => { setActiveTab(p.key); setCurrentPage(1); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${activeTab === p.key ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'}`}
                >
                  {p.icon && <span className={activeTab === p.key ? 'rounded bg-white p-0.5' : ''}>{p.icon}</span>}
                  {p.label}
                  <span className={`text-xs ${activeTab === p.key ? 'text-white/80' : 'text-gray-400'}`}>{tabCounts[p.key] ?? 0}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <Input
                placeholder={t('campaigns.filters.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="w-5 h-5" />}
              />
            </div>
            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className={selectCls}
                aria-label={t('campaigns.table.status') as string}
              >
                <option value="">{t('campaigns.filters.allStatus')}</option>
                <option value="active">{t('campaigns.filters.active')}</option>
                <option value="paused">{t('campaigns.filters.paused')}</option>
                <option value="draft">{t('campaigns.filters.draft')}</option>
              </select>
              <select
                value={dateRange}
                onChange={(e) => { setDateRange(e.target.value as any); setCurrentPage(1); }}
                className={selectCls}
                aria-label={t('campaigns.dateFilter.createdPeriod', 'Criado em') as string}
              >
                <option value="all">{t('campaigns.dateFilter.anyDate', 'Criadas em qualquer data')}</option>
                <option value="7d">{t('campaigns.dateFilter.last7', 'Criadas nos últimos 7 dias')}</option>
                <option value="30d">{t('campaigns.dateFilter.last30', 'Criadas nos últimos 30 dias')}</option>
                <option value="90d">{t('campaigns.dateFilter.last90', 'Criadas nos últimos 90 dias')}</option>
                <option value="custom">{t('campaigns.dateFilter.custom', 'Personalizado')}</option>
              </select>
            </div>
          </div>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              {[['start', customStartDate, setCustomStartDate], ['end', customEndDate, setCustomEndDate]].map(([k, v, set]: any, i) => (
                <React.Fragment key={k}>
                  {i === 1 && <span className="text-gray-500 dark:text-gray-400 text-sm">{t('campaigns.dateFilter.to', 'até')}</span>}
                  <input
                    type="date"
                    value={v}
                    min={k === 'end' ? customStartDate : undefined}
                    max={k === 'start' ? (customEndDate || new Date().toISOString().split('T')[0]) : new Date().toISOString().split('T')[0]}
                    onChange={(e) => set(e.target.value)}
                    className={`flex-1 sm:flex-none min-w-0 px-3 py-2 text-base sm:text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 ${customStartDate && customEndDate && customStartDate > customEndDate ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                  />
                </React.Fragment>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            {isLoadingMetrics ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3" />}
            {dateRange === 'all'
              ? t('campaigns.dateFilter.metricsHint30', 'Gasto, cliques e conversões dos últimos 30 dias')
              : t('campaigns.dateFilter.metricsHintPeriod', 'Gasto, cliques e conversões do período escolhido')}
          </p>
        </div>
      </Card>

      {/* Lista de campanhas */}
      {isLoading ? (
        <Card padding={false}>
          <div className="p-4 sm:p-8 space-y-3">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />)}
          </div>
        </Card>
      ) : filteredCampaigns.length === 0 ? (
        <Card>
          <CardContent className="pt-10 pb-10 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('campaigns.empty.title')}</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {search || statusFilter ? t('campaigns.empty.filterHint') : t('campaigns.empty.createHint')}
            </p>
            <Button onClick={handleNewCampaign} leftIcon={<Plus className="w-4 h-4" />}>{t('campaigns.actions.create')}</Button>
          </CardContent>
        </Card>
      ) : (
        <Card padding={false} className="overflow-hidden">
          {/* Celular: um cartão por campanha */}
          <ul className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {filteredCampaigns.map((campaign) => (
              <li key={campaign.id} className="p-3">
                <div className="flex items-start gap-3">
                  {platformIcons(campaign)}
                  <div className="min-w-0 flex-1">
                    <Link to={`/campaigns/${campaign.id}`} className="font-semibold text-gray-900 dark:text-white line-clamp-2 break-words">
                      {campaign.name}
                    </Link>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {statusBadge(campaign)}
                      {publishInfo(campaign)}
                    </div>
                  </div>
                </div>
                <Link to={`/campaigns/${campaign.id}`} className="grid grid-cols-3 gap-2 mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2 text-center">
                  <div>
                    <p className="text-[11px] text-gray-500">{t('campaigns.table.spend')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{formatCurrency(getMetric(campaign, 'spend', 'total_spent'))}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500">{t('campaigns.table.clicks')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatNumber(getMetric(campaign, 'clicks', 'total_clicks'))}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500">{t('campaigns.table.conversions', 'Conv.')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatNumber(getMetric(campaign, 'conversions', 'total_conversions'))}</p>
                  </div>
                </Link>
                <div className="flex items-center justify-between mt-2">
                  <Link to={`/campaigns/${campaign.id}`} className="text-sm font-semibold text-primary-600 flex items-center gap-1">
                    {t('campaigns.actions.view', 'Ver detalhes')} <ChevronRight className="w-4 h-4" />
                  </Link>
                  {rowActions(campaign, true)}
                </div>
              </li>
            ))}
          </ul>

          {/* Computador: tabela */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">{t('campaigns.table.campaign')}</th>
                  <th className="px-3 py-3 text-center">{t('campaigns.table.status')}</th>
                  <th className="hidden lg:table-cell px-3 py-3 text-left whitespace-nowrap">{t('campaigns.table.trend', 'Gasto 14d')}</th>
                  <th className="px-3 py-3 text-right">{t('campaigns.table.spend')}</th>
                  <th className="hidden xl:table-cell px-3 py-3 text-right">{t('campaigns.table.impressions')}</th>
                  <th className="px-3 py-3 text-right">{t('campaigns.table.clicks')}</th>
                  <th className="px-3 py-3 text-right">{t('campaigns.table.conversions', 'Conv.')}</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap" title={t('campaigns.google.allConversionsTitle', 'Todas as conversões (Google Ads)') as string}>{t('campaigns.google.allConvShort', 'Todas conv.')}</th>
                  <th className="px-3 py-3 text-right">CTR</th>
                  <th className="hidden 2xl:table-cell px-3 py-3 text-right whitespace-nowrap">CPC méd.</th>
                  <th className="hidden 2xl:table-cell px-3 py-3 text-right whitespace-nowrap">{t('campaigns.table.costPerConversion', 'Custo/Conv.')}</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3 min-w-[260px]">
                      <div className="flex items-center gap-3">
                        {platformIcons(campaign)}
                        <div className="min-w-0">
                          <Link to={`/campaigns/${campaign.id}`} className="font-medium text-gray-900 dark:text-white hover:text-primary-600 line-clamp-2 break-words" title={campaign.name}>
                            {campaign.name}
                          </Link>
                          <div className="mt-0.5">{publishInfo(campaign)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">{statusBadge(campaign)}</td>
                    <td className="hidden lg:table-cell px-3 py-3">{showValues ? <Sparkline data={daily?.series_por_campanha?.[campaign.id] || []} /> : null}</td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(getMetric(campaign, 'spend', 'total_spent'))}</td>
                    <td className="hidden xl:table-cell px-3 py-3 text-right text-gray-600 dark:text-gray-400">{formatNumber(getMetric(campaign, 'impressions', 'total_impressions'))}</td>
                    <td className="px-3 py-3 text-right text-gray-600 dark:text-gray-400">{formatNumber(getMetric(campaign, 'clicks', 'total_clicks'))}</td>
                    <td className="px-3 py-3 text-right text-gray-600 dark:text-gray-400">{formatNumber(getMetric(campaign, 'conversions', 'total_conversions'))}</td>
                    <td className="px-3 py-3 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{googleAllConversions(campaign)}</td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900 dark:text-white">
                      {(metricsLoaded ? (metricsMap[campaign.id]?.ctr ?? 0) * 100 : (campaign.ctr || 0)).toFixed(2)}%
                    </td>
                    <td className="hidden 2xl:table-cell px-3 py-3 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {(() => { const v = metricsLoaded ? (metricsMap[campaign.id]?.cpc ?? 0) : 0; return v > 0 ? formatCurrency(v) : '-'; })()}
                    </td>
                    <td className="hidden 2xl:table-cell px-3 py-3 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {(() => { const v = metricsLoaded ? (metricsMap[campaign.id]?.costPerConversion ?? 0) : 0; return v > 0 ? formatCurrency(v) : '-'; })()}
                    </td>
                    <td className="px-2 py-3 w-px">
                      <div className="flex items-center justify-end gap-0.5">
                        <Link to={`/campaigns/${campaign.id}`} className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg text-gray-500 hover:text-blue-600" title={t('campaigns.actions.view', 'Ver detalhes')}>
                          <Eye className="w-4 h-4" />
                        </Link>
                        {rowActions(campaign)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="flex items-center justify-between gap-3 px-3 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-3">
              <span>
                {((currentPage - 1) * rowsPerPage) + 1}–{Math.min(currentPage * rowsPerPage, totalCampaigns)} {t('campaigns.pagination.of', 'de')} {totalCampaigns}
              </span>
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}
                className="hidden sm:block px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                aria-label={t('campaigns.pagination.rowsPerPage', 'Linhas por página:') as string}
              >
                {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} {t('campaigns.pagination.perPage', 'por página')}</option>)}
              </select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 disabled:opacity-40" aria-label={t('common.previous', 'Anterior') as string}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-2 tabular-nums">{currentPage} / {totalPages}</span>
                <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 disabled:opacity-40" aria-label={t('common.next', 'Próxima') as string}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Contas conectadas e saldo (informação de apoio, depois da lista) */}
      <Card padding={false}>
        <button onClick={() => setShowContas((v) => !v)} className="w-full flex items-center justify-between gap-3 p-3 sm:p-4 text-left">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex -space-x-1">{connected.map((p) => <span key={p.key} className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-white dark:border-gray-900 flex items-center justify-center">{p.icon}</span>)}</div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{t('campaigns.balances.accountsTitle', 'Contas de anúncio e saldo')}</span>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showContas ? 'rotate-90' : ''}`} />
        </button>
        {showContas && (
          <div className="px-3 sm:px-4 pb-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {hasMetaAds && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><MetaAdsIcon /><span className="text-sm font-medium text-gray-900 dark:text-white">Meta Ads</span><CheckCircle className="w-4 h-4 text-green-600 ml-auto" /></div>
                  {balances.meta?.available ? (() => {
                    const meta = balances.meta!;
                    const fmt = (v: number, c?: string) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: c || meta.currency || 'BRL' }).format(v);
                    const isPrepaid = !!meta.is_prepaid;
                    const promoTotal = (meta.promotional_credits || []).reduce((sum, c) => sum + (c.amount || 0), 0);
                    return (
                      <>
                        {meta.balance != null ? (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{isPrepaid ? t('campaigns.balances.metaAvailable', 'Saldo disponível') : t('campaigns.balances.metaPending', 'A cobrar no próximo ciclo')}</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{showValues ? fmt(meta.balance) : 'R$ •••••'}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.balances.metaPostpaid', 'Conta pós-paga (sem saldo)')}</p>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-0.5">
                          {isPrepaid && meta.total_loaded != null && <div>{t('campaigns.balances.totalLoaded', 'Total carregado')}: {showValues ? fmt(meta.total_loaded) : '•••••'}</div>}
                          {meta.amount_spent != null && <div>{t('campaigns.balances.lifetimeSpent', 'Gasto total')}: {showValues ? fmt(meta.amount_spent) : '•••••'}</div>}
                          {meta.spend_cap != null && meta.spend_cap > 0 && <div>{t('campaigns.balances.cap', 'Limite')}: {showValues ? fmt(meta.spend_cap) : '•••••'}</div>}
                          {meta.funding_source && !isPrepaid && <div className="truncate" title={meta.funding_source}>{meta.funding_source}</div>}
                          {promoTotal > 0 && <div className="text-emerald-600 dark:text-emerald-400 font-medium pt-1">{t('campaigns.balances.promoCredits', 'Créditos promocionais')}: {showValues ? fmt(promoTotal) : '•••••'}</div>}
                          {meta.extended_credit && (meta.extended_credit.available > 0 || meta.extended_credit.allocated > 0) && (
                            <div>{t('campaigns.balances.extendedCredit', 'Crédito BM')}: {showValues ? fmt(meta.extended_credit.available, meta.extended_credit.currency) : '•••••'}</div>
                          )}
                        </div>
                      </>
                    );
                  })() : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{balances.meta?.reason || t('campaigns.balances.unavailable', 'Indisponível')}</p>
                  )}
                </div>
              )}
              {hasGoogleAds && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><GoogleAdsIcon /><span className="text-sm font-medium text-gray-900 dark:text-white">Google Ads</span><CheckCircle className="w-4 h-4 text-green-600 ml-auto" /></div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{balances.google?.reason || t('campaigns.balances.googleNoApi', 'Google Ads não expõe saldo via API')}</p>
                </div>
              )}
              {hasLinkedInAds && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><LinkedInAdsIcon /><span className="text-sm font-medium text-gray-900 dark:text-white">LinkedIn Ads</span><CheckCircle className="w-4 h-4 text-green-600 ml-auto" /></div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{balances.linkedin?.reason || t('campaigns.balances.linkedinNoApi', 'LinkedIn Ads não expõe saldo via API')}</p>
                </div>
              )}
              {hasOpenaiAds && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><OpenAIAdsIcon /><span className="text-sm font-medium text-gray-900 dark:text-white">ChatGPT Ads</span><CheckCircle className="w-4 h-4 text-green-600 ml-auto" /></div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('campaigns.balances.openaiNoApi', 'ChatGPT Ads não expõe saldo via API — acompanhe o gasto pelas métricas')}</p>
                </div>
              )}
            </div>
            {notConnected.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {notConnected.map((p) => (
                  <button key={p.key} onClick={() => handleConnectAccount(p.key)} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100">
                    {p.icon} {t('campaigns.accounts.connect', 'Conectar')} {p.label} <Plus className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Conectar campanhas ao assistente de IA (MCP) */}
      <McpConnectCard />

      <ConsolidatedReportModal
        open={showConsolidatedModal}
        onClose={() => setShowConsolidatedModal(false)}
        campaigns={campaigns.map(c => ({
          id: c.id,
          name: c.name,
          status: c.status,
          platform: (c as any).platform,
          platforms: (c as any).platforms,
          total_spent: c.total_spent,
        }))}
      />
    </div>
  );
};

export default CampaignsPage;
